/**
 * File: isocheckPage.js
 * Origin: isotect
 * Description: [TODO: Description]
 *
 * Version: v4.0.1
 * Created: 2025-12-04
 * Last Updated: 2026-02-18
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Images
import uiDashboard from "../../assets/images/ui-dashboard.svg";
import uiHero from "../../assets/images/ui-hero.jpg";

// Icons
import { FaLock } from "react-icons/fa6";
import { FaRegCalendarCheck } from "react-icons/fa6";
import { FaShieldHalved } from "react-icons/fa6";
import { MdMarkEmailRead } from "react-icons/md";

// Isocheck Page
export const isocheckPage = {
    meta: {
        id: "isocheck-page",
    },
    hero: {
        image: uiHero,
        headline: "Take Control of Your QAQC Workflow",
        subheadline:
            "Stay ahead of coordination issues and never miss a critical comment again.",
        primaryButton: {
            text: "Get Started",
            type: "primary",
        },
        secondaryButton: {
            text: "Send Inquiry",
            type: "secondary",
        },
    },
    planCard: {
        title: "Subscription Plans",
        plans: [
            {
                id: "plan-qr-1",
                header: "Annual Subscription",
                cost: 450,
                currency: "USD",
                model: "/seat",
            },
            {
                id: "plan-qr-2",
                header: "Monthly Subscription",
                cost: 50,
                currency: "USD",
                model: "/seat",
            },
        ],
        features: [
            {
                id: "feature-qr-1",
                icon: <FaRegCalendarCheck />,
                text: "30-Day Money-Back Guarantee",
            },
            {
                id: "feature-qr-2",
                icon: <MdMarkEmailRead />,
                text: "Priority Email Support",
            },
            {
                id: "feature-qr-3",
                icon: <FaShieldHalved />,
                text: "Enterprise-grade Security",
            },
            {
                id: "feature-qr-4",
                icon: <FaLock />,
                text: "100% Data Privacy",
            },
        ],
    },
    articles: [
        {
            id: "article-qr-1",
            reverse: false,
            image: uiDashboard,
            headline: "Track and Resolve QA Comments with Ease",
            description:
                "Centralize all QA review feedback in one intuitive platform. Collaborate effortlessly, monitor progress, and ensure every issue is addressed - on time and with full transparency.",
        },
        {
            id: "article-qr-2",
            reverse: true,
            image: uiDashboard,
            headline: "Unify Feedback Across Teams",
            description:
                "No more scattered emails or chaotic spreadsheets. Bring all QA comments, revisions, and resolutions into one place to improve team alignment and reduce miscommunication.",
        },
        {
            id: "article-qr-3",
            reverse: false,
            image: uiDashboard,
            headline: "Outsource QA Reviews to Experts",
            description:
                "Delegate your QA reviews to licensed professional engineers. Save internal resources, ensure accuracy, and deliver higher quality work without stretching your team thin.",
        },
    ],
    faq: [
        {
            id: "faq-qr-1",
            header: "What is Isocheck and how does it work?",
            content:
                "Isocheck is a centralized QAQC and markup management platform that helps teams track comments, coordinate revisions, and ensure nothing falls through the cracks. Upload project PDFs, assign comments, standardize review workflows, and monitor progress from a single dashboard.",
        },
        {
            id: "faq-qr-2",
            header: "Who is Isocheck designed for?",
            content:
                "Isocheck is built for professionals across the Architecture, Engineering, and Construction (AEC) industry including architects, engineers, drafters, project managers, and contractors who need a reliable way to track comments and manage drawing updates.",
        },
        {
            id: "faq-qr-3",
            header: "What makes Isocheck different from traditional PDF markup tools?",
            content:
                "Most PDF tools only provide basic markups. Isocheck goes further by offering structured workflows, comment assignment, customizable review libraries, progress tracking, notifications, and cross-team coordination. It’s built specifically for AEC QAQC, not just redlining.",
        },
        {
            id: "faq-qr-4",
            header: "How does my team use Isocheck day-to-day?",
            content:
                "Teams upload project drawings, create or import comment libraries, assign markups to individuals, and track resolution through project dashboards. Flags highlight urgent issues, admins monitor project health, and automatic notifications keep everyone aligned and accountable.",
        },
        {
            id: "faq-qr-5",
            header: "Can Isocheck support outsourced QA reviews?",
            content:
                "Yes. You can delegate QA reviews directly to licensed professional engineers through our platform. This ensures high-quality feedback, technical accuracy, and reduced internal workload, ideal during crunch periods or staffing shortages.",
        },
        {
            id: "faq-qr-6",
            header: "Can Isocheck be customized to match our internal QA standards?",
            content:
                "Absolutely. Teams can build or import standardized comment libraries, review templates, and workflows tailored to their processes. This helps maintain consistency across projects, disciplines, and team members.",
        },
    ],
    cta: {
        label: "Explore Coordination Services",
        headline: "Supercharge Your QAQC Process",
        description:
            "Guaranteed to reduce design errors, cut rework time, and keep your team laser-focused",
    },
};
